import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  NgZone,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { bodyMap3dCalibrationForGender } from './body-map-3d.calibration';
import { BodyMap3dCanvas, BodyMap3dTap } from './body-map-3d.canvas';
import { BodyMap3dProjector } from './body-map-3d.projector';
import { BodyMapRegion, BodyMapView } from './body-map.types';
import {
  BodyMapMarker,
  bodyMapInitialView,
  bodyMapModelAsset,
  parseBodyMapMarker,
} from './body-map-marker';

@Component({
  selector: 'app-body-map-viewer',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './body-map-viewer.component.html',
  styleUrl: './body-map-viewer.component.scss',
})
export class BodyMapViewerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedRegionId?: string | null;
  @Input() gender?: string | number | null;
  @Input() bodyMapModel?: string | null;
  @Input() bodyMapView?: string | null;
  @Input() bodyMapX?: number | null;
  @Input() bodyMapY?: number | null;
  @Input() bodyMapZ?: number | null;
  @Output() regionSelected = new EventEmitter<string>();

  regions: BodyMapRegion[] = [];
  view: BodyMapView = 'FRONT';
  loaded = false;
  use3dFailed = false;
  modelReady = false;

  private loadedCatalogUrl?: string;
  private readonly projector = new BodyMap3dProjector();
  private readonly canvas = new BodyMap3dCanvas();
  private readonly isBrowser: boolean;
  private canvasHost: HTMLElement | null = null;

  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (!this.isBrowser) {
      this.use3dFailed = true;
    }
  }

  ngOnInit(): void {
    this.loadCatalog();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gender']) {
      this.loadCatalog();
      this.syncModel();
    }
    if (changes['selectedRegionId']) {
      this.syncViewToSelection();
      if (!this.selectedRegionId?.trim() && !this.preciseMarker) {
        this.canvas.clearSelectionMarker();
      }
    }
    if (
      changes['bodyMapModel'] ||
      changes['bodyMapView'] ||
      changes['bodyMapX'] ||
      changes['bodyMapY'] ||
      changes['bodyMapZ']
    ) {
      this.use3dFailed = false;
      this.syncViewToSelection();
      this.syncModel();
      this.syncMarker();
    }
  }

  ngOnDestroy(): void {
    this.canvas.dispose();
  }

  get visibleRegions(): BodyMapRegion[] {
    return this.regions.filter((region) => region.view === this.view);
  }

  get displayRegionId(): string | null {
    return this.selectedRegionId?.trim() || null;
  }

  get hasSelection(): boolean {
    return !!this.displayRegionId;
  }

  get preciseMarker(): BodyMapMarker | null {
    return parseBodyMapMarker(this);
  }

  get hasPreciseMarker(): boolean {
    return this.preciseMarker !== null;
  }

  setView(view: BodyMapView): void {
    this.view = view;
    if (!this.use3dFailed) {
      this.canvas.setCameraView(view);
    }
  }

  onSvgRegionClick(regionId: string): void {
    this.emitSelection(regionId);
  }

  @ViewChild('canvasHost')
  set canvasHostRef(host: ElementRef<HTMLElement> | undefined) {
    const element = host?.nativeElement ?? null;
    if (!this.isBrowser) {
      return;
    }
    if (!element) {
      if (this.canvasHost) {
        this.canvas.dispose();
        this.canvasHost = null;
      }
      return;
    }
    if (this.canvasHost === element && this.canvas.isReady) {
      this.syncModel();
      return;
    }
    this.canvasHost = element;
    this.mountCanvas(element);
  }

  private mountCanvas(element: HTMLElement): void {
    this.use3dFailed = false;
    this.modelReady = false;
    this.canvas.onTap = (tap) => this.zone.run(() => this.onCanvasTap(tap));
    this.canvas.setInteractive(false);
    this.canvas.onReady = () => {
      this.zone.run(() => {
        this.modelReady = true;
        this.canvas.setCameraView(this.view);
        this.syncMarker();
        this.cdr.detectChanges();
      });
    };
    this.canvas.onError = () => {
      this.zone.run(() => {
        this.use3dFailed = true;
        this.modelReady = false;
        this.canvas.dispose();
        this.canvasHost = null;
        this.cdr.detectChanges();
      });
    };
    try {
      this.canvas.mount(element);
      this.syncModel();
    } catch {
      this.use3dFailed = true;
      this.canvasHost = null;
    }
  }

  private syncModel(): void {
    const url = this.modelUrl();
    if (!this.canvasHost || this.use3dFailed) {
      return;
    }
    this.canvas.loadModel(url);
    this.syncMarker();
  }

  private modelUrl(): string {
    const marker = this.preciseMarker;
    if (marker) {
      return bodyMapModelAsset(marker);
    }
    return this.usesFemaleCatalog()
      ? 'assets/body-map-3d/female_base_rev1.glb'
      : 'assets/body-map-3d/human1.gltf';
  }

  private onCanvasTap(tap: BodyMap3dTap): void {
    const regionId = this.projector.hitTestRegion({
      regions: this.regions,
      view: this.view,
      x: tap.x,
      y: tap.y,
      z: tap.z,
      nx: tap.nx,
      ny: tap.ny,
      nz: tap.nz,
      cameraZ: tap.cameraZ,
      calibration: bodyMap3dCalibrationForGender(this.usesFemaleCatalog()),
    });
    if (!regionId) {
      return;
    }
    if (regionId.startsWith('POST_')) {
      this.setView('BACK');
    } else if (regionId.startsWith('ANT_')) {
      this.setView('FRONT');
    }
    this.emitSelection(regionId);
    this.cdr.detectChanges();
  }

  private emitSelection(regionId: string): void {
    this.regionSelected.emit(regionId);
  }

  private catalogUrl(): string {
    return this.usesFemaleCatalog()
      ? 'assets/body-map/body_map_catalog_female.json'
      : 'assets/body-map/body_map_catalog.json';
  }

  private usesFemaleCatalog(): boolean {
    const marker = this.preciseMarker;
    if (marker) {
      return marker.model === 'female';
    }
    const raw = String(this.gender ?? '').trim().toLowerCase();
    return raw === 'female' || raw === 'f' || raw === '1' || raw === '3';
  }

  private loadCatalog(): void {
    const url = this.catalogUrl();
    if (this.loadedCatalogUrl === url && this.loaded) {
      this.syncViewToSelection();
      return;
    }

    this.loadedCatalogUrl = url;
    this.loaded = false;
    this.http.get<BodyMapRegion[]>(url).subscribe({
      next: (regions) => {
        if (this.loadedCatalogUrl !== url) return;
        this.regions = regions ?? [];
        this.loaded = true;
        this.syncViewToSelection();
      },
      error: () => {
        if (this.loadedCatalogUrl !== url) return;
        this.loaded = true;
        this.use3dFailed = true;
      },
    });
  }

  private syncViewToSelection(): void {
    const marker = this.preciseMarker;
    if (marker) {
      this.view = bodyMapInitialView(marker);
      this.canvas.setCameraView(this.view);
      this.syncMarker();
      return;
    }
    const id = this.selectedRegionId?.trim() ?? '';
    if (id.startsWith('POST_')) {
      this.view = 'BACK';
      this.canvas.setCameraView('BACK');
    } else if (id.startsWith('ANT_')) {
      this.view = 'FRONT';
      this.canvas.setCameraView('FRONT');
    }
  }

  private syncMarker(): void {
    const marker = this.preciseMarker;
    this.canvas.setSelectionMarker(marker);
  }
}
